import React, {
  useMemo,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react'
import { useParams } from 'react-router-dom'
import { useRouteMatch } from 'react-router'
import {
  GetPreviewDetailsQuery,
  useGetPreviewPageQuery,
  useGetPreviewPrototypesQuery,
  GetPreviewPageQuery,
  GetPreviewPrototypesQuery,
  useRenderPresentationMutation,
  useGetPreviewDetailsLazyQuery,
} from '@sketch/gql-types'
import { RouteParams, routes } from '@sketch/modules-common'

export interface PreviewContextProps {
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
  currentPage?: GetPreviewPageQuery['preview']['page']
  prototypes?: GetPreviewPrototypesQuery['preview']['prototypes']['entries']
  isLoadingPage: boolean
  hasErrorLoading: boolean
  password?: string
}

export const PreviewContext = React.createContext<
  PreviewContextProps | undefined
>(undefined)

type PreviewContextProviderProps = {
  children: React.ReactNode
  preview: NonNullable<GetPreviewDetailsQuery['preview']>
  password?: string
}

export function PreviewContextProvider({
  children,
  preview,
  password,
}: PreviewContextProviderProps) {
  const { pageUUID } = useParams<RouteParams<'PREVIEW_PAGE_VIEW'>>()

  // Check if we're in the preview prototype player route
  const isPreviewPrototypePlayerRoute = !!useRouteMatch({
    path: routes.PREVIEW_PROTOTYPE_PLAYER.template(),
    exact: true,
  })

  const firstPageUUID = preview.pages.entries[0]?.uuid
  const currentPageUUID = pageUUID || firstPageUUID || undefined

  const { data, loading, error } = useGetPreviewPageQuery({
    variables: {
      previewIdentifier: preview.identifier,
      pageUUID: currentPageUUID!,
      password,
    },
    skip: !currentPageUUID,
  })

  const prototypeData = useGetPreviewPrototypesQuery({
    variables: {
      previewIdentifier: preview.identifier,
      password,
    },
    skip: !isPreviewPrototypePlayerRoute,
  })

  const prototypes = prototypeData.data?.preview.prototypes.entries || undefined
  const currentPage = currentPageUUID && data ? data.preview.page : undefined

  const contextValue = useMemo(() => {
    return {
      preview,
      currentPage,
      prototypes,
      isLoadingPage: loading,
      hasErrorLoading: !!error,
      password,
    }
  }, [preview, currentPage, error, loading, prototypes, password])

  const [presentationStatus, setPresentationStatus] = useState(
    preview.presentationStatus ?? 'PENDING'
  )
  const hasPresentationFile = presentationStatus === 'COMPLETED'

  // Polling configuration
  const pollInterval = 5000 // 5 seconds
  const pollTimeout = 5 * 60 * 1000 // 5 minutes
  const isPollingRef = useRef(false)
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [renderPresentation] = useRenderPresentationMutation({
    onError: 'do-nothing',
  })

  const [
    pollPresentationStatus,
    { data: pollData, stopPolling, startPolling },
  ] = useGetPreviewDetailsLazyQuery({
    variables: {
      previewIdentifier: preview.identifier,
      password,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  })

  // Update presentation status when poll data changes
  useEffect(() => {
    if (pollData?.preview?.presentationStatus) {
      setPresentationStatus(pollData.preview.presentationStatus)
    }
  }, [pollData])

  // Helper function to stop polling and clean up
  const cleanupPolling = useCallback(() => {
    if (stopPolling && isPollingRef.current) {
      stopPolling()
      isPollingRef.current = false
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }, [stopPolling])

  // Stop polling when presentation is completed
  useEffect(() => {
    if (presentationStatus === 'COMPLETED') {
      cleanupPolling()
    }
  }, [presentationStatus, cleanupPolling])

  // Kick off render mutation and start polling if presentation is not ready
  useEffect(() => {
    if (preview && !hasPresentationFile && !isPollingRef.current) {
      isPollingRef.current = true

      // Trigger presentation rendering
      renderPresentation({
        variables: { previewIdentifier: preview.identifier },
      })

      // Start polling for status updates
      pollPresentationStatus().then(() => {
        if (startPolling) {
          startPolling(pollInterval)
        }
      })

      // Set timeout to stop polling after pollTimeout duration
      pollTimeoutRef.current = setTimeout(() => {
        cleanupPolling()
      }, pollTimeout)
    }

    // Cleanup on unmount
    return () => {
      cleanupPolling()
    }
  }, [
    preview,
    hasPresentationFile,
    renderPresentation,
    pollPresentationStatus,
    startPolling,
    pollInterval,
    pollTimeout,
    cleanupPolling,
  ])

  return (
    <PreviewContext.Provider value={contextValue}>
      {children}
    </PreviewContext.Provider>
  )
}

export function usePreview() {
  const contextValue = useContext(PreviewContext)

  return contextValue
}

export function useIsPreviewView() {
  const contextValue = useContext(PreviewContext)

  return Boolean(contextValue)
}
